import { useContext, useEffect, useState } from 'react';
import {
  GlobalContext,
  SET_APP_VISIT,
  SET_FAJITA_FORM,
  OPEN_WIDGET,
} from '@mummssoftware/global-context/GlobalContext';
import {
  isElectionAddendumOffered,
  withElectionAddendumOffered,
} from '@mummssoftware/utils/utils';
import {
  FAJITA,
  Forms as FormsWidget,
  InProgress,
} from '@mummssoftware/utils/constants';
import { Context } from '../../context';
import { VisitContext } from '../Context/contexts/VisitContext';
import { HCPCSContext } from '../Context/contexts/HCPCSContext';
import { FORM_NAME, SET_SAVED_VISIT } from '../Context/constants';
import { useElectionAddendum } from './ElectionAddendumContext';
import { resolveElectionAddendumForm } from './resolveElectionAddendumForm';
import { shouldAutoOpenElectionAddendum } from './electionAddendumPromptRules';
import defaults from '../../translations/en.json';

/**
 * EA prompt state and Yes/No handlers. Opens as soon as the visit dialog is up;
 * Yes/No stay disabled (spinner) until visit bootstrap is settled so PUT does
 * not race start-visit hydration. When it auto-opens is decided by
 * `shouldAutoOpenElectionAddendum` from the visit's EA status (see
 * ElectionAddendumContext); the visit-header EA chip can always open it.
 */
export const useElectionAddendumPrompt = () => {
  const {
    patient,
    pim,
    agency,
    keycloak,
    currentUser,
    // Agency catalog from getVisitAttributes — insurance if savedVisit has no
    // EAO/EAC row. Create visits_v2 with [] already returns those rows; then
    // withElectionAddendumOffered only sets isSelected and does not need this.
    visitAttributes: visitAttributeCatalog,
  } = useContext(Context);
  const {
    visitOpen,
    minimizeVisitDialog,
    closingPriorVisit,
    savedVisit,
    hopeVisitWarning,
    showConfirmVisit,
    dispatchVisit,
  } = useContext(VisitContext);
  const {
    appVisit,
    appVisitDetails,
    // Prefer patientGlobal: EA tag / form reset call refreshGlobalPatientHeader,
    // which updates GlobalContext only — dashboard Context.patient stays stale.
    patientGlobal,
    dispatch: dispatchGlobal,
  } = useContext(GlobalContext);
  const { showVisitLoader, showHCPCS } = useContext(HCPCSContext);
  const {
    status,
    iconPromptOpen,
    closeIconPrompt,
    dismissedVisitId,
    setDismissedVisitId,
  } = useElectionAddendum();

  const requiredDateShifted = (patientGlobal ?? patient)?.eaDetails
    ?.requiredDateShifted;
  const visitId = savedVisit?.id ?? null;
  // Prefer appVisit (forms SUBMIT updates it); fall back to savedVisit.
  const eaOffered = isElectionAddendumOffered(appVisit ?? savedVisit);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [openingForm, setOpeningForm] = useState(false);

  const visitDialogVisible =
    !!visitOpen && !minimizeVisitDialog && !closingPriorVisit;

  const visitIdsAligned =
    visitId != null &&
    appVisit?.id != null &&
    String(visitId) === String(appVisit.id);

  const visitDetailsReady =
    visitId != null &&
    appVisitDetails != null &&
    String(appVisitDetails.id) === String(visitId);

  // Visit UI + pimreader details settled — safe to PUT EAO / open form.
  const visitBootstrapReady =
    visitIdsAligned &&
    visitDetailsReady &&
    !showVisitLoader &&
    !hopeVisitWarning &&
    !showConfirmVisit &&
    !showHCPCS;

  // Show as soon as the visit dialog has a saved visit; spinner until ready.
  const autoOpen = shouldAutoOpenElectionAddendum({
    status,
    visitDialogVisible,
    visitId,
    eaOffered,
    dismissedVisitId,
  });

  // Keep dialog open while Yes/No is in flight — otherwise applyEAOfferedLocally
  // flips eaOffered and autoOpen closes the dialog before the spinner can show.
  const open = autoOpen || iconPromptOpen || openingForm;
  const actionsDisabled = !visitBootstrapReady || openingForm;
  const showLoader = !visitBootstrapReady || openingForm;

  useEffect(() => {
    if (open) {
      setLoadError(null);
    }
  }, [open]);

  const dismiss = () => {
    if (visitId != null) {
      setDismissedVisitId(visitId);
    }
    setLoadError(null);
    closeIconPrompt();
  };

  const applyEAOfferedLocally = (visitPayload?: mumms.PatientVisit) => {
    const baseSaved = visitPayload ?? savedVisit;
    if (!baseSaved) {
      return;
    }
    const visitAttributes = withElectionAddendumOffered(
      baseSaved.visitAttributes,
      visitAttributeCatalog,
    );
    const nextSaved = { ...baseSaved, visitAttributes };
    dispatchVisit({
      type: SET_SAVED_VISIT,
      payload: nextSaved,
    });
    dispatchGlobal({
      type: SET_APP_VISIT,
      payload: appVisit
        ? {
            ...appVisit,
            visitAttributes: withElectionAddendumOffered(
              appVisit.visitAttributes,
              visitAttributeCatalog,
            ),
          }
        : nextSaved,
    });
  };

  /**
   * No only: stamp EAO (offered, not creating). Yes opens the form without EAO;
   * EAC is set on form submit when the patient is still owed.
   * Updates savedVisit / appVisit only after PUT succeeds.
   * @returns false when the PUT failed and `loadError` is showing.
   */
  const persistEAOffered = async (): Promise<boolean> => {
    if (!savedVisit || !patient?.patientNumber) {
      return true;
    }
    // Already persisted (chip reopen) — skip PUT.
    if (eaOffered) {
      return true;
    }

    const nextVisit = {
      ...savedVisit,
      visitAttributes: withElectionAddendumOffered(
        savedVisit.visitAttributes,
        visitAttributeCatalog,
      ),
    };

    try {
      const res = await pim.updatePatientVisit(
        patient.patientNumber,
        nextVisit,
        agency,
      );
      if (res instanceof Response) {
        setLoadError(defaults.electionAddendumOfferedUpdateError);
        return false;
      }

      // TEMP: re-apply EAO on the response until visit PUT persists EAO.
      // Remove when backend returns visitAttributes with EAO selected.
      applyEAOfferedLocally({
        ...res,
        visitAttributes: withElectionAddendumOffered(
          (res as mumms.PatientVisit).visitAttributes,
          visitAttributeCatalog,
        ),
      } as mumms.PatientVisit);
      return true;
    } catch {
      setLoadError(defaults.electionAddendumOfferedUpdateError);
      return false;
    }
  };

  const onNo = async () => {
    if (actionsDisabled) {
      return;
    }
    if (!savedVisit || !patient?.patientNumber) {
      dismiss();
      return;
    }

    setLoadError(null);
    setOpeningForm(true);
    try {
      const offered = await persistEAOffered();
      if (!offered) {
        return;
      }
      dismiss();
    } finally {
      setOpeningForm(false);
    }
  };

  const onYes = async () => {
    if (!patient || !keycloak || !currentUser || actionsDisabled) {
      return;
    }

    const levelOfCare =
      appVisit?.levelofcareshortname || patient.loc || undefined;

    setLoadError(null);
    setOpeningForm(true);
    try {
      const listRow = await resolveElectionAddendumForm({
        keycloak,
        pim,
        agency,
        currentUser,
        patient,
        levelOfCare,
      });

      if (!listRow) {
        setLoadError(defaults.electionAddendumUnavailable);
        return;
      }

      const isInProgress = listRow.statusforpatientperson === InProgress;
      const fajitaForm = ({
        ...listRow,
        patientnumber: patient.patientNumber,
        patientid: String(patient.patientId ?? patient.id ?? ''),
        isInProgressFAJITA: isInProgress,
      } as unknown) as mumms.FajitaForm;

      dispatchGlobal({
        type: SET_FAJITA_FORM,
        payload: fajitaForm,
      });
      localStorage.setItem(FORM_NAME, `${FAJITA}:${listRow.formkey}`);
      dispatchGlobal({
        type: OPEN_WIDGET,
        payload: { componentName: FormsWidget },
      });

      dismiss();
    } catch (e) {
      console.error('Election Addendum: form list load failed', e);
      setLoadError(defaults.electionAddendumLoadError);
    } finally {
      setOpeningForm(false);
    }
  };

  return {
    open,
    requiredDateShifted,
    actionsDisabled,
    showLoader,
    loadError,
    onYes,
    onNo,
  };
};
